/**
 * DependencySidebar — always-on dependency-annotation pip rendered alongside
 * the per-objectKind editors. Surfaces incoming-FK / dependent-view / outgoing-
 * FK summary counts plus a small list of representative items so the user can
 * see at a glance whether destructive edits will hit dependents.
 *
 * Backing data flows through `useDependencyAnnotations` — introspection-
 * fallback primary; NavFeatures Phase 2 (#11) is the upgrade path.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice H2 — Dependency annotations.
 */
import type { ReactNode } from 'react';
import { ArrowDownToLine, ArrowUpFromLine, Cog, Eye, ListTree, Loader2 } from 'lucide-react';

import type { DependencyAnnotations } from '../../dependencies/types';
import { DependencyDependentList } from './DependencyDependentList';

export const DEPENDENCY_SIDEBAR_TESTID = 'vqe-ddl-dependency-sidebar';

export interface DependencySidebarProps {
  readonly annotations: DependencyAnnotations;
  readonly isLoading?: boolean;
  readonly error?: Error | null;
  /** Render-mode hint — `'connected'` shows the live pip; `'disconnected'`
   *  shows a small "Not connected" caveat (the editor is parsed-only / Edit-
   *  as-SQL flow). Defaults to `'connected'`. */
  readonly mode?: 'connected' | 'disconnected';
  readonly testId?: string;
}

export function DependencySidebar({
  annotations,
  isLoading = false,
  error = null,
  mode = 'connected',
  testId = DEPENDENCY_SIDEBAR_TESTID,
}: DependencySidebarProps) {
  const summary = annotations.summary;

  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-border bg-card/40 p-3 text-xs"
      data-testid={testId}
      data-incoming-fk-count={summary.incomingFkCount}
      data-dependent-view-count={summary.dependentViewCount}
      data-dependent-routine-count={summary.dependentRoutineCount}
      data-outgoing-fk-count={summary.outgoingFkCount}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 font-medium uppercase tracking-wide text-muted-foreground">
          <ListTree className="h-3.5 w-3.5" />
          Dependencies
        </div>
        {isLoading ? (
          <Loader2
            className="h-3.5 w-3.5 animate-spin text-muted-foreground"
            data-testid={`${testId}-loading`}
          />
        ) : null}
      </div>

      {mode === 'disconnected' ? (
        <p
          className="text-[11px] text-muted-foreground"
          data-testid={`${testId}-disconnected`}
        >
          Connect to a database to surface dependent objects.
        </p>
      ) : null}

      {error ? (
        <p
          className="break-words text-[11px] text-destructive"
          data-testid={`${testId}-error`}
        >
          {error.message}
        </p>
      ) : null}

      {annotations.limitedSupport ? (
        <p
          className="text-[11px] italic text-muted-foreground"
          data-testid={`${testId}-limited-support`}
        >
          Engine reports limited dependency tracking — counts may be incomplete.
        </p>
      ) : null}

      <SummaryRow
        icon={<ArrowDownToLine className="h-3 w-3" />}
        label="Incoming FKs"
        count={summary.incomingFkCount}
        testId={`${testId}-incoming-fk-count`}
      />
      <SummaryRow
        icon={<Eye className="h-3 w-3" />}
        label="Dependent views"
        count={summary.dependentViewCount}
        testId={`${testId}-dependent-view-count`}
      />
      <SummaryRow
        icon={<Cog className="h-3 w-3" />}
        label="Dependent routines"
        count={summary.dependentRoutineCount + summary.dependentTriggerCount}
        testId={`${testId}-dependent-routine-count`}
      />
      <SummaryRow
        icon={<ArrowUpFromLine className="h-3 w-3" />}
        label="Outgoing FKs"
        count={summary.outgoingFkCount}
        testId={`${testId}-outgoing-fk-count`}
      />

      {annotations.incoming.length > 0 ? (
        <DependencyDependentList
          heading="Used by"
          items={annotations.incoming}
          testId={`${testId}-incoming-list`}
        />
      ) : null}
      {annotations.outgoing.length > 0 ? (
        <DependencyDependentList
          heading="References"
          items={annotations.outgoing}
          testId={`${testId}-outgoing-list`}
        />
      ) : null}

      {summary.totalIncoming === 0 && summary.totalOutgoing === 0 && mode === 'connected' && !isLoading && !error ? (
        <p
          className="text-[11px] text-muted-foreground"
          data-testid={`${testId}-empty`}
        >
          No dependents found.
        </p>
      ) : null}
    </div>
  );
}

interface SummaryRowProps {
  readonly icon: ReactNode;
  readonly label: string;
  readonly count: number;
  readonly testId: string;
}

function SummaryRow({ icon, label, count, testId }: SummaryRowProps) {
  return (
    <div className="flex items-center justify-between gap-2 text-[11px]">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        {icon}
        {label}
      </span>
      <span
        className="font-mono tabular-nums text-foreground"
        data-testid={testId}
      >
        {count}
      </span>
    </div>
  );
}

